import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../ui/utils';

interface QuickAction {
  /**
   * Action icon component
   */
  icon: React.ComponentType<{ className?: string }>;
  /**
   * Action label (shown in tooltip)
   */
  label: string;
  /**
   * Click handler
   */
  onClick: () => void;
  /**
   * Visual variant
   * @default 'default'
   */
  variant?: 'default' | 'success' | 'danger';
}

interface FloatingActionButtonProps {
  /**
   * List of quick actions
   */
  actions: QuickAction[];
  className?: string;
}

/**
 * FloatingActionButton (FAB) - Mobile-first quick action menu
 * 
 * Displays a floating button that expands to show multiple quick actions.
 * Commonly used for primary actions like "Add Transaction" or "Add Item".
 * 
 * Features:
 * - Backdrop overlay when open
 * - Smooth animations
 * - Customizable action variants
 * - ARIA labels for accessibility
 * 
 * @example
 * ```tsx
 * <FloatingActionButton
 *   actions={[
 *     {
 *       icon: Plus,
 *       label: 'Nova Receita',
 *       onClick: () => navigate('/income/new'),
 *       variant: 'success',
 *     },
 *     {
 *       icon: Minus,
 *       label: 'Nova Despesa',
 *       onClick: () => navigate('/expense/new'),
 *       variant: 'danger',
 *     },
 *   ]}
 * />
 * ```
 */
export function FloatingActionButton({ actions, className }: FloatingActionButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  const handleActionClick = (action: QuickAction) => {
    action.onClick();
    setIsOpen(false);
  };

  const getVariantColors = (variant?: QuickAction['variant']) => {
    switch (variant) {
      case 'success':
        return 'bg-success-600 hover:bg-success-700 text-white';
      case 'danger':
        return 'bg-danger-600 hover:bg-danger-700 text-white';
      default:
        return 'bg-primary hover:bg-primary/90 text-primary-foreground';
    }
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm animate-in fade-in"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
      
      <div className={cn('fixed bottom-6 right-6 z-50 md:bottom-8 md:right-8', className)}>
        {/* Action buttons */}
        {isOpen && (
          <div className="absolute bottom-16 right-0 flex flex-col gap-3">
            {actions.map((action, index) => {
              const Icon = action.icon;
              return (
                <div
                  key={index}
                  className="flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <span className="rounded-lg bg-popover px-3 py-2 text-sm shadow-lg border border-border whitespace-nowrap">
                    {action.label}
                  </span>
                  <Button
                    size="icon"
                    className={cn(
                      'h-12 w-12 rounded-full shadow-lg',
                      getVariantColors(action.variant)
                    )}
                    onClick={() => handleActionClick(action)}
                    aria-label={action.label}
                  >
                    <Icon className="h-5 w-5" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Main FAB */}
        <Button
          size="icon"
          className={cn(
            'h-14 w-14 rounded-full shadow-xl transition-all',
            isOpen ? 'bg-muted hover:bg-muted/90' : 'bg-primary hover:bg-primary/90'
          )}
          onClick={toggleMenu}
          aria-label={isOpen ? 'Fechar menu' : 'Abrir menu de ações rápidas'}
        >
          {isOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Plus className="h-6 w-6" />
          )}
        </Button>
      </div>
    </>
  );
}
